package com.trevorwiebe.timesheet.admin.loadrate.domain.model

import com.trevorwiebe.timesheet.core.domain.formatCentsAsDollars
import com.trevorwiebe.timesheet.mileage.domain.model.Location

enum class LoadRateCategory { BULK, HOPPER, BELT, AUGER, VAN }

enum class LoadRateType { FLAT_RATE, PER_UNIT }

// The bushel variants are test weights: 56 lb (corn), 60 lb (soybeans/wheat), 100 lb.
enum class LoadRateUnit { MILE, HOUR, BUSHEL_56, BUSHEL_60, BUSHEL_100, TON }

data class LoadRate(
    val id: String,
    val rateCategory: LoadRateCategory,
    val rateType: LoadRateType,
    val unit: LoadRateUnit? = null,
    val perUnit: Int = 0,
    val flatRate: Int = 0,
    val pickupLocationId: String = "",
    val dropLocationId: String = "",
    val lastUpdated: String = "",
) {
    val label: String
        get() = when (rateType) {
            LoadRateType.FLAT_RATE ->
                if (flatRate > 0) {
                    "${rateCategory.displayName} — Flat rate $${flatRate.formatCentsAsDollars()}"
                } else {
                    "${rateCategory.displayName} — Flat rate"
                }
            LoadRateType.PER_UNIT ->
                if (perUnit > 0) {
                    "${rateCategory.displayName} — $${perUnit.formatCentsAsDollars()}/${unit?.displayName ?: "unit"}"
                } else {
                    "${rateCategory.displayName} — Per ${unit?.displayName ?: "unit"}"
                }
        }

    fun routeLabel(locations: List<Location>): String {
        if (pickupLocationId.isEmpty() && dropLocationId.isEmpty()) return ""
        val names = locations.associateBy({ it.id }, { it.name })
        val pickup = if (pickupLocationId.isEmpty()) "—" else names[pickupLocationId] ?: "Unknown"
        val drop = if (dropLocationId.isEmpty()) "—" else names[dropLocationId] ?: "Unknown"
        return "$pickup → $drop"
    }
}

val LoadRateCategory.displayName: String
    get() = name.lowercase().replaceFirstChar { it.uppercase() }

val LoadRateType.displayName: String
    get() = when (this) {
        LoadRateType.FLAT_RATE -> "Flat rate"
        LoadRateType.PER_UNIT -> "Per unit"
    }

val LoadRateUnit.displayName: String
    get() = when (this) {
        LoadRateUnit.MILE -> "mile"
        LoadRateUnit.HOUR -> "hour"
        LoadRateUnit.BUSHEL_56 -> "bushel (56)"
        LoadRateUnit.BUSHEL_60 -> "bushel (60)"
        LoadRateUnit.BUSHEL_100 -> "bushel (100)"
        LoadRateUnit.TON -> "ton"
    }

/** Pounds per one unit for weight-based units; null for time/distance units. */
val LoadRateUnit.poundsPerUnit: Double?
    get() = when (this) {
        LoadRateUnit.TON -> 2000.0
        LoadRateUnit.BUSHEL_56 -> 56.0
        LoadRateUnit.BUSHEL_60 -> 60.0
        LoadRateUnit.BUSHEL_100 -> 100.0
        LoadRateUnit.MILE, LoadRateUnit.HOUR -> null
    }
